import type { FuelType } from '@/types/fuel-transactions'
import type { AircraftTableRow } from '@/types/aircrafts'

// ============================================
// Types
// ============================================

/** Canonical field names we map CSV columns to */
export type FuelCsvField =
  | 'invoice_date'
  | 'invoice_number'
  | 'customer_name'
  | 'tail_number'
  | 'product'
  | 'gallons'
  | 'unit_price'
  | 'total_amount'

/** Mapping from CSV header index to canonical field */
export type ColumnMapping = Map<number, FuelCsvField>

export type RowStatus = 'ready' | 'warning' | 'error' | 'duplicate'

export interface ParsedFuelRow {
  rowIndex: number
  status: RowStatus
  errors: string[]
  warnings: string[]

  // Raw CSV values
  rawDate: string
  rawTailNumber: string
  rawCustomerName: string
  rawProduct: string
  rawGallons: string
  rawUnitPrice: string
  rawTotalAmount: string
  rawInvoiceNumber: string

  // Resolved values (null if resolution failed)
  invoiceDate: string | null       // YYYY-MM-DD
  tailNumber: string | null
  fuelType: FuelType | null
  gallons: number | null
  unitPrice: number | null
  totalAmount: number | null       // From CSV, for display only
  invoiceNumber: string | null

  // Linked entities
  aircraftId: string | null
  clientId: string | null
  leaseContractId: string | null
  clientName: string | null        // Resolved from aircraft owner
}

export interface CsvParseResult {
  headers: string[]
  mapping: ColumnMapping
  rows: ParsedFuelRow[]
  summary: {
    total: number
    ready: number
    warnings: number
    errors: number
    duplicates: number
  }
}

export interface ResolveContext {
  aircraft: AircraftTableRow[]
  contracts: ActiveContractInfo[]
  existingExternalRefs: Set<string>
  locationId: string
}

export interface ActiveContractInfo {
  id: string
  contract_number: string
  client_id: string
  start_date: string | null
  end_date: string | null
  aircraft_ids: string[]
}

// ============================================
// Column detection aliases
// ============================================

const COLUMN_ALIASES: Record<FuelCsvField, string[]> = {
  invoice_date: ['invoice date', 'date', 'fueled date', 'fueling date', 'transaction date', 'txn date'],
  invoice_number: ['invoice number', 'invoice #', 'invoice no', 'inv #', 'inv no', 'reference', 'ref #'],
  customer_name: ['customer name', 'client', 'customer', 'client name', 'account name', 'buyer'],
  tail_number: ['tail number', 'tail #', 'tail no', 'registration', 'aircraft', 'reg #', 'n-number', 'n number'],
  product: ['product', 'fuel type', 'fuel', 'product name', 'fuel grade', 'type'],
  gallons: ['gallons', 'quantity', 'qty', 'volume', 'gal', 'amount (gal)'],
  unit_price: ['unit price', 'price/gal', 'price per gallon', 'price', 'rate', 'ppg', '$/gal'],
  total_amount: ['total amount', 'total', 'amount', 'extended amount', 'ext amount', 'line total', 'invoice amount'],
}

/**
 * Auto-detect column mapping from CSV headers
 */
export function detectColumnMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = new Map()
  const usedFields = new Set<FuelCsvField>()

  for (let i = 0; i < headers.length; i++) {
    const normalized = headers[i].trim().toLowerCase()

    for (const [field, aliases] of Object.entries(COLUMN_ALIASES) as [FuelCsvField, string[]][]) {
      if (usedFields.has(field)) continue
      if (aliases.some((alias) => normalized === alias)) {
        mapping.set(i, field)
        usedFields.add(field)
        break
      }
    }
  }

  return mapping
}

// ============================================
// Fuel product mapping
// ============================================

const JET_A_PATTERNS = [
  'jet a', 'jet-a', 'jeta', 'jet a + additive', 'jet a+', 'jet fuel',
  'jet a-1', 'jet a1', 'jet-a1',
]

const AVGAS_PATTERNS = [
  '100ll', 'avgas', 'avgas 100ll', 'av gas', '100 ll', 'avgas100ll',
]

/**
 * Map CSV product string to fuel_type enum
 */
export function mapFuelProduct(product: string): FuelType | null {
  const normalized = product.trim().toLowerCase()

  if (JET_A_PATTERNS.some((p) => normalized.includes(p))) return 'jet_a'
  if (AVGAS_PATTERNS.some((p) => normalized.includes(p))) return 'avgas'

  return null
}

// ============================================
// Date parsing
// ============================================

/**
 * Parse various date formats into YYYY-MM-DD
 */
export function parseFuelDate(dateStr: string): string | null {
  const trimmed = dateStr.trim()
  if (!trimmed) return null

  // Try YYYY-MM-DD
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (isoMatch) {
    const [, y, m, d] = isoMatch
    return formatDateParts(parseInt(y), parseInt(m), parseInt(d))
  }

  // Try MM/DD/YYYY or M/D/YYYY
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (slashMatch) {
    const [, m, d, y] = slashMatch
    return formatDateParts(parseInt(y), parseInt(m), parseInt(d))
  }

  // Try MM-DD-YYYY
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/)
  if (dashMatch) {
    const [, m, d, y] = dashMatch
    return formatDateParts(parseInt(y), parseInt(m), parseInt(d))
  }

  return null
}

function formatDateParts(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 2000 || year > 2100) {
    return null
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// ============================================
// Row resolution
// ============================================

/**
 * Resolve parsed CSV rows against org data (aircraft, contracts, existing refs).
 * Pure logic — no Supabase calls.
 */
export function resolveImportRows(
  rawRows: Record<string, string>[],
  mapping: ColumnMapping,
  _headers: string[],
  context: ResolveContext
): ParsedFuelRow[] {
  // Build lookup maps
  const aircraftByTail = new Map<string, AircraftTableRow>()
  for (const ac of context.aircraft) {
    aircraftByTail.set(ac.tail_number.toUpperCase().trim(), ac)
  }

  // Build aircraft → contract lookup
  const contractByAircraftId = new Map<string, ActiveContractInfo>()
  for (const contract of context.contracts) {
    for (const acId of contract.aircraft_ids) {
      contractByAircraftId.set(acId, contract)
    }
  }

  // Reverse mapping: field → column index
  const fieldToIndex = new Map<FuelCsvField, number>()
  for (const [idx, field] of mapping.entries()) {
    fieldToIndex.set(field, idx)
  }

  return rawRows.map((row, i) => {
    const errors: string[] = []
    const warnings: string[] = []

    // Extract raw values using mapping
    const values = Object.values(row)
    const getRaw = (field: FuelCsvField): string => {
      const idx = fieldToIndex.get(field)
      if (idx === undefined) return ''
      return (values[idx] ?? '').trim()
    }

    const rawDate = getRaw('invoice_date')
    const rawTailNumber = getRaw('tail_number')
    const rawCustomerName = getRaw('customer_name')
    const rawProduct = getRaw('product')
    const rawGallons = getRaw('gallons')
    const rawUnitPrice = getRaw('unit_price')
    const rawTotalAmount = getRaw('total_amount')
    const rawInvoiceNumber = getRaw('invoice_number')

    // 1. Parse date
    const invoiceDate = parseFuelDate(rawDate)
    if (!invoiceDate && rawDate) errors.push('Invalid date format')
    if (!rawDate) errors.push('Missing date')

    // 2. Map product → fuel_type
    let fuelType: FuelType | null = null
    if (rawProduct) {
      fuelType = mapFuelProduct(rawProduct)
      if (!fuelType) errors.push(`Unknown fuel type: "${rawProduct}"`)
    } else {
      errors.push('Missing fuel type')
    }

    // 3. Parse gallons
    const gallons = parsePositiveNumber(rawGallons)
    if (gallons === null && rawGallons) errors.push('Invalid gallons value')
    if (!rawGallons) errors.push('Missing gallons')
    if (gallons !== null && gallons <= 0) errors.push('Gallons must be positive')

    // 4. Parse unit price
    const unitPrice = parsePositiveNumber(rawUnitPrice)
    if (unitPrice === null && rawUnitPrice) errors.push('Invalid unit price')
    if (!rawUnitPrice) errors.push('Missing unit price')

    // 5. Parse total (display only)
    const totalAmount = parsePositiveNumber(rawTotalAmount)

    // 6. Lookup tail number → aircraft
    let aircraftId: string | null = null
    let clientId: string | null = null
    let clientName: string | null = null
    let leaseContractId: string | null = null

    if (rawTailNumber) {
      const aircraft = aircraftByTail.get(rawTailNumber.toUpperCase().trim())
      if (aircraft) {
        aircraftId = aircraft.id
        // Use aircraft's owner_client_id as client
        clientId = aircraft.owner_client_id
        clientName = aircraft.owner_client_name
        if (!clientId) {
          warnings.push('Aircraft has no owner — client will be blank')
        }

        // Find active contract for this aircraft
        const contract = contractByAircraftId.get(aircraft.id)
        if (contract) {
          // Check date overlap if we have a parsed date
          if (invoiceDate && contract.start_date && contract.end_date) {
            if (invoiceDate >= contract.start_date && invoiceDate <= contract.end_date) {
              leaseContractId = contract.id
              // Override client from contract if different
              if (contract.client_id) {
                clientId = contract.client_id
              }
            } else {
              warnings.push('Transaction date outside contract period')
            }
          } else {
            // No date check possible — link anyway
            leaseContractId = contract.id
            if (contract.client_id) {
              clientId = contract.client_id
            }
          }
        } else {
          warnings.push('No active lease contract found')
        }
      } else {
        errors.push(`Aircraft "${rawTailNumber}" not found`)
      }
    } else {
      errors.push('Missing tail number')
    }

    // 7. Check dedup
    const invoiceNumber = rawInvoiceNumber || null
    let isDuplicate = false
    if (invoiceNumber && context.existingExternalRefs.has(invoiceNumber)) {
      isDuplicate = true
    }

    // Determine status
    let status: RowStatus = 'ready'
    if (isDuplicate) {
      status = 'duplicate'
    } else if (errors.length > 0) {
      status = 'error'
    } else if (warnings.length > 0) {
      status = 'warning'
    }

    return {
      rowIndex: i + 1,
      status,
      errors,
      warnings,
      rawDate,
      rawTailNumber,
      rawCustomerName,
      rawProduct,
      rawGallons,
      rawUnitPrice,
      rawTotalAmount,
      rawInvoiceNumber,
      invoiceDate,
      tailNumber: rawTailNumber || null,
      fuelType,
      gallons: gallons !== null && gallons > 0 ? gallons : null,
      unitPrice,
      totalAmount,
      invoiceNumber,
      aircraftId,
      clientId,
      leaseContractId,
      clientName,
    }
  })
}

/**
 * Parse a string as a positive number, stripping currency symbols and commas
 */
function parsePositiveNumber(str: string): number | null {
  if (!str) return null
  const cleaned = str.replace(/[$,\s]/g, '')
  const num = parseFloat(cleaned)
  if (isNaN(num)) return null
  return num
}

/**
 * Compute summary from parsed rows
 */
export function computeSummary(rows: ParsedFuelRow[]): CsvParseResult['summary'] {
  let ready = 0
  let warnings = 0
  let errors = 0
  let duplicates = 0

  for (const row of rows) {
    switch (row.status) {
      case 'ready': ready++; break
      case 'warning': warnings++; break
      case 'error': errors++; break
      case 'duplicate': duplicates++; break
    }
  }

  return { total: rows.length, ready, warnings, errors, duplicates }
}
